import {
  blueColour,
  defaultFontItalic,
  defaultFontNormal,
} from "../utils/styling";
import {
  generateSectionParagraphs,
  getSectionHeader,
} from "../utils/WordExportUtils";

export const getNameOfReturnParagraphs = (data) => {
  const { poaData, reportingCompanyName } = data;

  const paragraphs = [
    {
      condition: !poaData?.reporting_company,
      segments: [
        {
          text: "No CIR return has been prepared for the period, on the basis that no reporting company nomination is in place for the period, and this is required in order to submit a return.",
          font: defaultFontNormal,
        },
        {
          text: "As the group has a disallowance in the period of account, a full Corporate Interest Restriction return must be submitted.",
          font: defaultFontNormal,
          break: 2,
        },
      ],
    },
    {
      condition: poaData.group_disallowance > 0 && reportingCompanyName,
      segments: [
        {
          text: "As the group has a disallowance in the period of account, a full Corporate Interest Restriction return must be submitted, and a full return has therefore been prepared on this basis.",
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    {
      condition: poaData.return_status?.isRevised,
      segments: [
        {
          text: "As the group had previously filed a CIR return with HMRC, an amended return has been prepared. The return includes an explanation of the ways in which it differs from the original return – this is required in the case of a revised return.",
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    {
      condition:
        poaData.group_disallowance === 0 &&
        poaData.return_status?.isRevised &&
        poaData?.return_status?.type === "Full",
      segments: [
        {
          text: "As the group does not have a disallowance in the period of account, an abbreviated CIR return can be submitted where an election to this effect is made. The return has been prepared as a full interest restriction return.",
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    {
      condition:
        poaData.group_disallowance === 0 &&
        ["Abbreviated", "Amended - Abbreviated"].includes(
          poaData?.return_status?.type,
        ) &&
        reportingCompanyName,
      segments: [
        {
          text: "As the group does not have a disallowance in the period of account, an abbreviated CIR return can be submitted where an election to this effect is made. The return has been prepared as an abbreviated interest restriction return.",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: "The submission of an abbreviated CIR return is administratively easier as, provided that the group has undertaken sufficient analysis to determine that no disallowance is required, it is not necessary to include a statement of calculations (including, inter alia, the group debt cap and Tax-Interest and Tax-EBITDA of UK group companies).",
          font: defaultFontNormal,
          break: 2,
        },
        {
          text: "[Relationship team to add commentary to outline work undertaken to ensure that there is no disallowance - e.g. why Tax-Interest is less than £2m, or why it is clear that 30% of Tax-EBITDA ",
          font: defaultFontNormal,
          color: blueColour,
        },
        {
          text: "and",
          font: defaultFontItalic,
          underline: true,
          color: blueColour,
        },
        {
          text: " the fixed ratio debt cap both exceed Tax-Interest. Note, that if full calculations have been undertaken to establish this, submission of a full CIR return may be appropriate]",
          font: defaultFontNormal,
          color: blueColour,
        },
        {
          text: "Where an abbreviated return is submitted, the group cannot carry forward any unused interest allowance (including any unused interest allowance from prior years). However, a revised return can be submitted within 5 years of the end of the period of account, which would allow for the unused interest allowance to be utilised in later periods.",
          font: defaultFontNormal,
          break: 2,
        },
      ],
    },
    {
      condition:
        poaData.group_disallowance === 0 &&
        poaData.return_status.type === "Abbreviated" &&
        poaData?.group_interest_reactivation_cap > 0 &&
        reportingCompanyName,
      segments: [
        {
          text: "The calculations reflect that the Interest Allowance exceeds the Tax-Interest expense for the period. It would therefore be possible to reactivate part of the group’s previously disallowed Tax-Interest expense. However, this is only possible where a full return is prepared for the period.",
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    {
      condition: poaData?.returnStatus?.contains_estimates,
      segments: [
        {
          text: "It is noted that the return has been prepared on the bases of estimated information, as set out below. Where this is the case, the estimated information must be updated and a revised return filed with HMRC within a period of 3 years following the end of the period of account. If this is not possible, it is necessary to notify HMRC that it has not been possible to update the estimated information within the required timeframe and give an estimate of when the information is likely to be corrected. If this is not done by the end of the 3 year period, a £500 penalty may be applied. The only exception is where the ANGIE figure has been estimated and it is not the restricting factor, in which case HMRC guidance confirms that providing this is appropriately disclosed, there will be no requirement to file an amended return or submit a notification.",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: "[include estimate wording from return]",
          font: defaultFontNormal,
          break: 2,
        },
      ],
    },
    {
      condition:
        poaData.group_disallowance === 0 &&
        ["Abbreviated", "Amended - Abbreviated", "Full"].includes(
          poaData?.return_status?.type && reportingCompanyName,
        ),
      segments: [
        {
          text: `[Relationship team to add commentary to explain why ${poaData?.return_status?.type === "Full" ? "a full return" : "an abbreviated return"} has been submitted (e.g. work needed to verify that no restriction was required meant that all numbers had already been determined / expectation of utilising in future years, so easier to complete now)].`,
          font: defaultFontNormal,
          color: blueColour,
          break: 1,
        },
      ],
    },
    {
      condition:
        poaData.group_disallowance === 0 &&
        !["Abbreviated", "Amended - Abbreviated"].includes(
          poaData.return_status.type && reportingCompanyName,
        ),
      segments: [
        {
          text: "As the group does not have a disallowance in the period of account, an abbreviated CIR return can be submitted where an election to this effect is made. The return has been prepared as a full interest restriction return.",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: "By filing a full return, the group can carry forward any unused interest allowance generated in the period and any brought forward unused interest allowance from prior years to the extent it has not expired.",
          font: defaultFontNormal,
          break: 2,
        },
      ],
    },
  ];

  return [
    getSectionHeader("Nature of Return"),
    ...generateSectionParagraphs(paragraphs),
  ];
};
